import React from 'react';
import { Header } from '@ui/components/Header';
import { Footer } from '@ui/components/Footer';

type GuideLandingPageProps = {
  onBeginApplication: () => void;
  onSignIn?: () => void;
  onLogout?: () => void;
};

const FEATURES = [
  {
    title: 'A Steady Stream of Clients',
    copy: 'We match you with clients who align with your specialties, values, and experience level — so your energy stays on the work, not on marketing.',
  },
  {
    title: 'Safety & Discretion',
    copy: "Your profile is never public. It's only shown to seekers who are a good fit, and you decide exactly what you offer, where, and how.",
  },
  {
    title: 'A Mission You Can Stand For',
    copy: "We believe psychedelic work thrives when grounded in preparation, integration, and human connection. If you share that vision, you're in the right place.",
  },
];

export function GuideLandingPage({ onBeginApplication, onSignIn, onLogout }: GuideLandingPageProps) {
  return (
    <div className="min-h-screen bg-[#FFFCEF] text-[#214D3C] flex flex-col">
      <Header variant="register" onLogout={onLogout} />

      <main className="flex-1 px-6 py-16 lg:px-[120px] lg:py-16">
        <div className="flex w-full flex-col gap-12">
          {/* Hero Section */}
          <section className="flex flex-col gap-8 max-w-[612px]">
            <h1 className="font-['Recoleta','Georgia',serif] text-[40px] md:text-[56px] leading-[1.1] text-[#214D3C]">
              Join a collective of trusted guides
            </h1>
            <p className="text-lg leading-[1.4] text-[#214D3C]">
              You're here because you care about people — and because you believe in the healing potential of
              psychedelic-supported growth. We're here to help you bring that work to more seekers who truly need it.
            </p>
          </section>

          {/* Features Section - 3 cards in a row */}
          <section className="flex flex-col gap-20">
            <div className="flex flex-col items-center lg:items-start lg:flex-row gap-5">
              {FEATURES.map((feature, index) => (
                <article key={index} className="flex-1 rounded-2xl p-6 min-h-[48px] overflow-hidden flex flex-col gap-2.5 max-w-[387px]">
                  <div className="flex flex-col gap-2 items-start justify-center w-full">
                    <img src="/icon.svg" alt="" className="w-6 h-6" />
                    <h4 className="font-['Recoleta','Georgia',serif] text-2xl leading-[1.6] text-[#214D3C]">
                      {feature.title}
                    </h4>
                  </div>
                  <p className="text-base leading-[1.2] text-[#214D3C]">{feature.copy}</p>
                </article>
              ))}
            </div>

            {/* Let's get you set up Card */}
            <div className="bg-[#214D3C] border border-[rgba(48,64,49,0.3)] rounded-2xl p-6 min-h-[48px] overflow-hidden flex flex-col gap-2.5 w-full max-w-[1195px]">
              <div className="flex flex-col gap-2 items-start justify-center w-full">
                <img src="/icon.svg" alt="" className="w-6 h-6 [filter:brightness(0)_invert(98%)_sepia(6%)_saturate(365%)_hue-rotate(338deg)_brightness(103%)]" />
                <h4 className="font-bold text-xl leading-[1.6] text-[#FFFCEF]">Let's get you set up</h4>
              </div>
              <p className="text-sm leading-[1.4] text-[#FFFCEF]">
                Whether you're a therapist, psychedelic facilitator, coach, or retreat leader, this application tells us
                about your modalities, experience, and comfort levels — so we can curate seeker requests that make sense
                for you. Your information remains private, secure, and used only for the matching process.
              </p>
            </div>
          </section>

          {/* CTA Section */}
          <section className="flex flex-col gap-8 max-w-[463px]">
            <button
              type="button"
              onClick={onBeginApplication}
              className="inline-flex items-center gap-4 rounded-full bg-[#214D3C] px-5 py-[18px] text-base leading-[1.06] text-[#FFFCEF] transition-transform duration-200 hover:-translate-y-0.5 hover:bg-[#1a3d2f] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#214D3C] w-fit"
            >
              <span>Begin Your Guide Application</span>
              <span className="text-sm" aria-hidden>
                →
              </span>
            </button>

            {onSignIn ? (
              <p className="text-base leading-[1.2] text-[#214D3C]">
                Already part of the collective?{' '}
                <button
                  type="button"
                  onClick={onSignIn}
                  className="font-bold text-[#214D3C] hover:underline underline-offset-4"
                >
                  Sign in
                </button>
              </p>
            ) : null}

            <p className="text-sm leading-[1.2] text-[#214D3C]/80">
              By continuing, you agree to our{' '}
              <a href="/terms/full" className="underline">
                Terms of Service
              </a>{' '}
              and{' '}
              <a href="/privacypolicy" className="underline">
                Privacy Policy
              </a>
            </p>
          </section>
        </div>
      </main>

      <Footer />
    </div>
  );
}
